import { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomizedDataGrid } from "components/customized_data_grid";
import { Column, FormatterProps } from "react-data-grid";
import { Box, Typography, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { getTaskRecordList } from 'service/unifiedAlerts';
import './TasksRecords.css';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getSnowRecordUrl } from './IncidentsTable';
import { snowUrl } from 'service/config';

interface Row {
  number: string;
  problem: string;
  parent: string;
  state: string;
  short_description: string;
  assignment_group: string;
  assigned_to: string;
  opened_by: string;
  priority: string;
  sys_id: string;
}

interface Paginate {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface TaskData {
  data: Row[];
  pagination: Paginate;
}

type EllipsisFormatterProps = {
  readonly value?: string | null;
};

function EllipsisFormatter({ value }: EllipsisFormatterProps) {
  return (
    <div title={value ?? ''} className="has-ellipsis">
      {value || '--'}
    </div>
  );
}

type StringRowKeys = 'number' | 'problem' | 'parent' | 'state' | 'short_description' | 'assignment_group' | 'assigned_to' | 'opened_by' | 'priority';

function createEllipsisFormatter(field: StringRowKeys) {
  return function Formatter(props: FormatterProps<Row>) {
    return <EllipsisFormatter value={props.row[field]} />;
  };
}

// Generate all individual formatters for your fields
const alertFormatter = createEllipsisFormatter('number');
const stateFormatter = createEllipsisFormatter('state');
const descFormatter = createEllipsisFormatter('short_description');
const assignmentGroupFormatter = createEllipsisFormatter('assignment_group');
const assignedToFormatter = createEllipsisFormatter('assigned_to');
const openedByFormatter = createEllipsisFormatter('opened_by');
const priorityFormatter = createEllipsisFormatter('priority');

const TasksRecords = ({parent, parentTile, state, age, sysId}: {parent: string | null, parentTile: string, state: string | undefined, age: string | null, sysId: string }) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [taskRecords, setTaskRecords] = useState<TaskData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortingInfo, setSortingInfo] = useState({ sortBy: 'sys_updated_on', sortDir: 'DESC' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const parentFormatter = createEllipsisFormatter(parentTile === 'Problem Tickets' ? 'problem' : 'parent');
  const renderAlertId = (number: string | null, sysId: string) => {
     if (!number) return null;
    return(
  <Typography
    variant="body2"
    sx={{
      color: "textLight.secondary",
      cursor: "pointer",
      textDecoration: "underline",
      display: "inline",
      fontWeight: 600,
    }}
    onClick={() =>
      window.open(getSnowRecordUrl(parentTile, sysId, snowUrl), "_blank")
    }
  >
    {number}
  </Typography>
);
  }

const SNOW_TABLE_MAP: Record<string, string> = {
  "Service Requests": "sc_task",
  "Change Requests": "change_task",
  "Problem Tickets": "problem_task",
};

  const openServiceNowRecord = (parentTile: string, sysId: string) => {
  const table = SNOW_TABLE_MAP[parentTile];

  if (!table || !sysId) return;

  const url = `${snowUrl}/now/sow/record/${table}/${sysId}`;
  window.open(url, "_blank");
};

    const renderAlertIDFormatter = ({ row }: { row: any }) => {
      return (
        <Typography
          variant='tableRowFont'
          sx={{
            color: "textLight.secondary",
            cursor: "pointer",
            textDecoration: "underline",
            display: "flex",
            alignItems: "center",
            justifyContent: 'space-between',
            gap: 0.5,
          }}
           onClick={() => {
            openServiceNowRecord(parentTile, row.sys_id);
          }}
        >
          {row.number}
        </Typography>
      );
    };

    // FIX 1: Wrap columns in useMemo so the DataGrid doesn't trigger layout recalculations
    const columns = useMemo(() => {
      const allColumns = [
        { key: 'number', name: 'ALERT ID', formatter: renderAlertIDFormatter, width: 180, resizable: true },
        { key: 'parent', name: 'PARENT ID', formatter: parentFormatter, width: 180, resizable: true },
        // { key: 'state', name: 'STATE', formatter: stateFormatter, width: 120, resizable: true },
        { key: 'short_description', name: 'DESCRIPTION', formatter: descFormatter, minWidth: 300, resizable: true },
        // {
        //   key: 'age',
        //   name: 'AGE',
        //   width: 120,
        //   formatter: renderAgeFormatter,
        // },
        { key: 'assignment_group', name: 'ASSIGNMENT GROUP', formatter: assignmentGroupFormatter, minWidth: 180, resizable: true },
        { key: 'assigned_to', name: 'ASSIGNED TO', formatter: assignedToFormatter, minWidth: 180, resizable: true },
        { key: 'opened_by', name: 'OPENED BY', formatter: openedByFormatter, minWidth: 180, resizable: true },
        { key: 'priority', name: 'PRIORITY', formatter: priorityFormatter, minWidth: 180, resizable: true },
      ];
  
      
      return allColumns;
  
    }, [parentTile]);

    // FIX 2: Wrap defaultColumnNames in useMemo to prevent array reference changes
  const defaultColumns = ['ALERT ID', 'PARENT ID',  'DESCRIPTION', 'ASSIGNMENT GROUP', 'ASSIGNED TO', 'OPENED BY', 'PRIORITY'];

  const fetchTaskRecords = async () => {
    // Avoid double loaders if already fetching
    if (isLoading) return;
    try {
      setIsLoading(true)
      const payload = { 
        page, 
        pageSize, 
        isExcelExport: false, 
        parentId: parent || null,
        parentTile: parentTile,
        sortBy: sortingInfo.sortBy,
        sortDir: sortingInfo.sortDir
      };
      const resp = await getTaskRecordList(payload);
      setTaskRecords(resp);
    } catch (e) {
      console.info(JSON.stringify(e));
    } finally {
      setIsLoading(false);
    }
  };

  // FIX 3: Use dynamic state dependencies safely 
  useEffect(() => {
    fetchTaskRecords();
  }, [page, pageSize, sortingInfo.sortBy, sortingInfo.sortDir]);

  // FIX 4: Safely build page change handler to prevent out-of-bounds loop syncing
  const handlePageChange = useCallback((_: any, p: number) => {
    if (p !== page) setPage(p);
  }, [page]);

  const handlePageSizeChange = useCallback((e: any) => {
    const size = Number.parseInt(e.target.value, 10);
    if (size !== pageSize) {
      setPageSize(size);
      setPage(1); // Reset to page 1 safely when size alters
    }
  }, [pageSize]);


    const handleExport = async () => {
      if (isExporting) return;
      setIsExporting(true);
      const minDelay = new Promise((resolve) => setTimeout(resolve, 2000));
      try {

        const payload = { 
            page: 1,
            pageSize: 10000,
            isExcelExport: true,
            parentId: parent || null,
            parentTile: parentTile,
            sortBy: sortingInfo.sortBy,
            sortDir: sortingInfo.sortDir
          };
          
           // 1. Wait for both the API call and the minimum delay timer
          const [resp] = await Promise.all([getTaskRecordList(payload), minDelay]);

          // 2. Extract data safely from the response structure
          const bufferData = resp?.buffer?.data;
          const contentType = resp?.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          const fileName = resp?.fileName || 'unified_tasks_data.xlsx';

          if (!bufferData || bufferData.length === 0) {
            throw new Error("No file data received from the server");
          }

          // 3. Convert the buffer array into a browser-compatible Uint8Array
          const byteArray = new Uint8Array(bufferData);
          
          // 4. Create a Blob object representing the file
          const blob = new Blob([byteArray], { type: contentType });

          // 5. Generate a temporary download URL
          const downloadUrl = window.URL.createObjectURL(blob);

          // 6. Create a temporary anchor element and trigger the download
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          
          document.body.appendChild(link);
          link.click();

          // 7. Clean up the DOM and memory
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
         
      } catch (error) {
        console.error('Error exporting alerts:', error);
      } finally {
        setIsExporting(false);
      }
    };

  return (
    <div  
      style={{
        width: '95%',
        background: 'white',
        marginTop: '2vh',
        marginBottom: '2vh',
        marginLeft: '5vh',
        marginRight: '5vh',
        // Inline variable overrides to injected grid styles
        // (Adjust the hex colors below to whatever you prefer)
        ['--rdg-row-hover-background-color' as any]: '#f0f7ff', 
        ['--rdg-cell-hover-background-color' as any]: '#e0f2fe' 
      }}
        className="custom-grid-container"
      >
        <Box
          sx={{
            width: "100%",
            background: 'white',
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            py: 1,
            borderRadius: 0,
            borderBottom: "2px solid #D9DDE3",
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              px: 1.5,
              flexWrap: "wrap",
              color: "text.primary",
              fontSize: 14,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.table.text,
                  opacity: 0.7,
                  fontWeight: 400,
                }}
              >
                {parentTile}
              </Typography>
              {renderAlertId(parent, sysId)}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.table.text,
                  opacity: 0.7,
                  fontWeight: 400,
                }}
              >
                Status
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.table.text,
                  fontWeight: 600,
                }}
              >
                {state}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.table.text,
                  opacity: 0.7,
                  fontWeight: 400,
                }}
              >
                Age
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.table.text,
                  fontWeight: 600,
                }}
              >
                {age}
              </Typography>
            </Box>
          </Box>

          {/* Right Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 32,
              px: 2,
            }}
            onClick={handleExport}
          >
            {isExporting ? (
              <CircularProgress size={20} />
            ) : (
              <FileDownloadIcon sx={{color: colors.table.moreIcon}}/>
            )}
          </Box>
        </Box>
        {taskRecords?.data && taskRecords?.pagination ? (
        <CustomizedDataGrid
          tableColumns={columns}
          columns={columns}
          tableData={taskRecords.data}
          defaultColumnNames={defaultColumns}
          listLength={taskRecords.pagination.total}
          page={page}
          pageSize={pageSize}
          paginationData={{
            totalElements: taskRecords.pagination.total,
            totalPages: Math.ceil(taskRecords.pagination.total / taskRecords.pagination.pageSize)
          }}
          onSorting={setSortingInfo}
          currSorting={sortingInfo}
          handlePageChange={handlePageChange}
          handlepageSizeChange={handlePageSizeChange}
          isFetching={isLoading}
          isDateRangeFilter={false}
          isDisabled={false}
          isShowMoreButton={false}
          pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
          isSortIcon={true}
        />
      ) : null}
      {/* <CaptureExportControls
          onScreenshot={handleScreenCapture}
          screenCapturing={screenCapturing}
          onExport={handleExport}
          isExporting={isExporting}
          showExport={true}
          isVisible={true}
          type="incident"
      /> */}
    </div>
  );
};

export default TasksRecords;
